/**
 * PageControlsContext
 *
 * Provides page-level controls (Favorite, Delete, Reprompt, Share, Copy) to the main navigation.
 * Used to show comparison detail actions in the mobile top navigation ellipsis menu.
 * Also provides comparison detail view state for mobile header buttons.
 */

import React, { createContext, useContext, useState, ReactNode } from 'react';
import type { ComparisonDetailView } from '../components/results/state/comparisonDetailView';

interface PageControlsContextType {
  // State
  showControls: boolean;
  isFavorited: boolean;
  navSlot?: ReactNode;

  // Comparison detail view state (for mobile header buttons)
  comparisonDetailView?: ComparisonDetailView;
  onComparisonDetailViewChange?: (view: ComparisonDetailView) => void;
  comparisonResponsesCount?: number;
  isResponseDetail?: boolean;

  setIsResponseDetail: (isDetail: boolean) => void;

  // Handlers
  onFavorite?: () => void;
  onDelete?: () => void;
  onReprompt?: () => void;
  onShare?: () => void;
  onCopy?: () => void;

  // Navigation slot (e.g., mobile-only header controls)
  setNavSlot: (slot: ReactNode | null) => void;

  // Setters (called by ComparisonDetail)
  setPageControls: (controls: {
    show: boolean;
    isFavorited?: boolean;
    onFavorite?: () => void;
    onDelete?: () => void;
    onReprompt?: () => void;
    onShare?: () => void;
    onCopy?: () => void;
    comparisonDetailView?: ComparisonDetailView;
    onComparisonDetailViewChange?: (view: ComparisonDetailView) => void;
    comparisonResponsesCount?: number;
    isResponseDetail?: boolean;
  }) => void;

  clearPageControls: () => void;
}

const PageControlsContext = createContext<PageControlsContextType | undefined>(undefined);

export const PageControlsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [showControls, setShowControls] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [navSlot, setNavSlot] = useState<ReactNode | null>(null);
  const [comparisonDetailView, setComparisonDetailView] = useState<ComparisonDetailView | undefined>(undefined);
  const [comparisonResponsesCount, setComparisonResponsesCount] = useState<number | undefined>(undefined);
  const [isResponseDetail, setIsResponseDetailState] = useState<boolean | undefined>(undefined);
  const [handlers, setHandlers] = useState<{
    onFavorite?: () => void;
    onDelete?: () => void;
    onReprompt?: () => void;
    onShare?: () => void;
    onCopy?: () => void;
    onComparisonDetailViewChange?: (view: ComparisonDetailView) => void;
  }>({});

  const setPageControls = (controls: Parameters<PageControlsContextType['setPageControls']>[0]) => {
    setShowControls(controls.show);
    setIsFavorited(controls.isFavorited ?? false);
    setComparisonDetailView(controls.comparisonDetailView);
    setComparisonResponsesCount(controls.comparisonResponsesCount);
    if (controls.isResponseDetail !== undefined) {
      setIsResponseDetailState(controls.isResponseDetail);
    }
    setHandlers({
      onFavorite: controls.onFavorite,
      onDelete: controls.onDelete,
      onReprompt: controls.onReprompt,
      onShare: controls.onShare,
      onCopy: controls.onCopy,
      onComparisonDetailViewChange: controls.onComparisonDetailViewChange,
    });
  };

  const clearPageControls = () => {
    setShowControls(false);
    setIsFavorited(false);
    setComparisonDetailView(undefined);
    setComparisonResponsesCount(undefined);
    setIsResponseDetailState(false);
    setHandlers({});
    setNavSlot(null);
  };

  const setIsResponseDetail = (isDetail: boolean) => {
    setIsResponseDetailState(isDetail);
  };

  return (
    <PageControlsContext.Provider
      value={{
        showControls,
        isFavorited,
        navSlot: navSlot ?? undefined,
        comparisonDetailView,
        comparisonResponsesCount,
        isResponseDetail,
        setIsResponseDetail,
        ...handlers,
        setPageControls,
        setNavSlot,
        clearPageControls,
      }}
    >
      {children}
    </PageControlsContext.Provider>
  );
};

export const usePageControls = () => {
  const context = useContext(PageControlsContext);
  if (!context) {
    throw new Error('usePageControls must be used within PageControlsProvider');
  }
  return context;
};
